// src/features/datev-weiterberechnung/services/datevInvoiceParserService.ts

/**
 * Parser für DATEV-Rechnungs-PDFs (monatliche Abrechnungen der DATEV eG).
 *
 * Extrahiert:
 * - Abrechnungsmonat/-jahr aus dem PDF-Header (z.B. "März 2026")
 * - Rechnungsnummer, -datum und Beraternummer
 * - Den Block "Beraterbezogene Leistungen" (Kosten der Kanzlei selbst)
 * - Pro Mandantenblock: DATEV-Mandantennummer, Name, alle Posten (Artikelnr, Menge,
 *   Leistungszeitpunkt, USt-Satz, Netto-Betrag)
 * - Den Nettobetrag aus dem Rechnungsfuß als Gegenprobe
 * - Zeilen, die wie ein Posten aussehen, aber nicht gelesen werden konnten
 *
 * Die Extraktion basiert auf pdfjs-dist (clientseitig) und nutzt dasselbe
 * Y-Koordinaten-basierte Zeilen-Rekonstruktions-Pattern wie payrollPdfAnalyzerService.
 */

import * as pdfjsLib from 'pdfjs-dist';
import {
  ParsedDatevInvoice,
  ParsedMandant,
  ParsedInvoicePosition,
  BERATER_BLOCK_KEY,
  BERATER_BLOCK_LABEL,
  EIGENE_FIRMA_BERATERNUMMER,
  EIGENE_FIRMA_MANDANTENNUMMER
} from '../types';

// Worker von CDN laden (vermeidet CRA-Bundling-Probleme – konsistent mit payrollPdfAnalyzerService)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ============================================
// KONSTANTEN
// ============================================

const GERMAN_MONTH_NAMES: Record<string, number> = {
  'januar': 1, 'februar': 2, 'märz': 3, 'maerz': 3, 'april': 4,
  'mai': 5, 'juni': 6, 'juli': 7, 'august': 8, 'september': 9,
  'oktober': 10, 'november': 11, 'dezember': 12
};

// Kategorie-Summen-Zeilen, die wir NICHT als Mandantenblock interpretieren dürfen
const CATEGORY_SUMMARY_KEYWORDS = [
  'Beraterbezogene Leistungen',
  'Summe Betriebliches Rechnungswesen',
  'Summe Personalwirtschaft',
  'Summe Komplettlösungen',
  'Summe IT-Lösungen und Security',
  'Summe Zusatzangebote',
  'Nettobetrag',
  'USt-Betrag',
  'Rechnungsendbetrag'
];

/**
 * Muster für den Leistungszeitpunkt einer Postenzeile.
 *
 * DATEV verwendet zwei Formen, und zwar innerhalb derselben Rechnung gemischt:
 * - ein Tagesdatum, z.B. "23.03.2026"
 * - eine Monatsangabe, z.B. "März 2026" oder abgekürzt "Aug. 2026"
 *
 * Die Abkürzung ist der Grund, warum hier bewusst kein Katalog von Monatsnamen
 * steht: DATEV kürzt lange Monatsnamen ab, kurze nicht, und die genaue
 * Schreibweise ist je Monat unterschiedlich. Ein fester Katalog würde bei jeder
 * unbekannten Schreibweise dazu führen, dass die Zeile stillschweigend verworfen
 * und der Posten nicht weiterberechnet wird. Deshalb wird jedes Wort vor einer
 * vierstelligen Jahreszahl akzeptiert. Der Wert selbst wird nirgends für Logik
 * verwendet, sondern nur mitgeführt; die Zuordnung zum Abrechnungsmonat erfolgt
 * ausschliesslich über den Rechnungskopf.
 */
const TIME_POINT_PATTERN =
  `(?:[A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß.]*\\s+\\d{4}|\\d{2}\\.\\d{2}\\.\\d{4})`;

/**
 * Muster für die Fußnotenmarkierung hinter der Artikelnummer.
 *
 * Hochgestellte Ziffern hängen in der Textextraktion direkt an der
 * Artikelnummer ("20640¹", "50010⁴"), stehen also ohne Leerzeichen davor.
 * Eine normale Ziffer als Fußnote wird weiterhin nur mit trennendem
 * Leerzeichen akzeptiert, damit sie nicht versehentlich die Menge anschneidet.
 */
const FOOTNOTE_PATTERN = `(?:\\s*[¹²³⁴⁵⁶⁷⁸⁹⁰]|\\s+\\d)?`;

/**
 * Erkennt Zeilen, die von der Form her eine Postenzeile sein müssen: Sie enden
 * auf einen Betrag und enthalten entweder einen USt-Satz oder den Verweis
 * "lt. Anlage". Passt eine solche Zeile auf keines der Parse-Muster, ist das ein
 * Befund und keine Nebensache, denn dahinter steht ein nicht erfasster Betrag.
 */
const CANDIDATE_POSITION_PATTERN = /(?:\d+\s*%|lt\.\s*Anlage).*?-?[\d.,]+$/i;

/** Nettobetrag im Rechnungsfuß, je USt-Satz eine eigene Zeile */
const NETTO_TOTAL_PATTERN = /^Nettobetrag\s+(-?[\d.,]+)$/i;

// ============================================
// TEXT-EXTRAKTION (Y-Koordinaten-basiert, pro Seite)
// ============================================

/**
 * Extrahiert Textzeilen aus einer einzelnen PDF-Seite.
 *
 * Kritisch: pdfjs-dist liefert Text-Items in der internen PDF-Reihenfolge,
 * NICHT in visueller Lesereihenfolge. Wir müssen daher zunächst nach Y-Koordinate
 * gruppieren (= eine "Zeile") und dann innerhalb jeder Gruppe nach X-Koordinate
 * sortieren, um die korrekte Spaltenreihenfolge zu erhalten.
 */
async function extractPageLines(pdf: pdfjsLib.PDFDocumentProxy, pageNum: number): Promise<string[]> {
  const page = await pdf.getPage(pageNum);
  const textContent = await page.getTextContent();

  // Schritt 1: Alle Items mit X- und Y-Koordinate sammeln
  interface Item { x: number; y: number; str: string; }
  const allItems: Item[] = [];
  for (const item of textContent.items) {
    if ('str' in item) {
      const transform = (item as { transform: number[] }).transform;
      const str = item.str;
      if (str.trim()) {
        allItems.push({ x: transform[4], y: transform[5], str });
      }
    }
  }

  // Schritt 2: Items nach Y-Koordinate gruppieren (Toleranz: 3pt)
  interface YGroup { y: number; items: Item[]; }
  const yGroups: YGroup[] = [];
  for (const item of allItems) {
    let group = yGroups.find(g => Math.abs(g.y - item.y) <= 3);
    if (!group) {
      group = { y: item.y, items: [] };
      yGroups.push(group);
    }
    group.items.push(item);
  }

  // Schritt 3: Y-Gruppen absteigend sortieren (PDF-Koordinaten: oben = hohes Y)
  yGroups.sort((a, b) => b.y - a.y);

  // Schritt 4: Pro Gruppe Items nach X sortieren und zur Zeile zusammenfügen
  const lines: string[] = [];
  for (const group of yGroups) {
    group.items.sort((a, b) => a.x - b.x);
    const line = group.items.map(it => it.str).join(' ').replace(/\s+/g, ' ').trim();
    if (line) lines.push(line);
  }

  return lines;
}

// ============================================
// HEADER-PARSING (Monat/Jahr, Rechnungsnummer, -datum)
// ============================================

/**
 * Sucht im PDF-Text nach dem Abrechnungsmonat.
 * Format im Header: "März 2026" (ohne weiteren Text in der Zeile)
 * oder als Teil von Zeilen wie "DATEV-Rechnung März 2026".
 *
 * Der Kopf schreibt den Monatsnamen aus, hier ist der Katalog also belastbar.
 * Anders als in den Postenzeilen ist der Wert hier zwingend erforderlich,
 * weil er den Abrechnungsmonat der gesamten Rechnung bestimmt.
 */
function extractMonthYear(lines: string[]): { month: number; year: number } | null {
  // Scanne die ersten 30 Zeilen nach "<Monatsname> <4-stellige Jahreszahl>"
  const scanLimit = Math.min(30, lines.length);
  const monthNamesRegex = Object.keys(GERMAN_MONTH_NAMES).join('|');
  const pattern = new RegExp(`\\b(${monthNamesRegex})\\s+(\\d{4})\\b`, 'i');

  for (let i = 0; i < scanLimit; i++) {
    const match = lines[i].match(pattern);
    if (match) {
      const monthName = match[1].toLowerCase();
      const year = parseInt(match[2], 10);
      const month = GERMAN_MONTH_NAMES[monthName];
      if (month && year >= 2020 && year <= 2099) {
        return { month, year };
      }
    }
  }

  return null;
}

/**
 * Extrahiert Rechnungsnummer aus Header-Bereich (optional)
 * Format: "Rechnungsnummer: 33793692026"
 */
function extractRechnungsnummer(lines: string[]): string | null {
  for (const line of lines.slice(0, 30)) {
    const match = line.match(/Rechnungsnummer:\s*(\d+)/i);
    if (match) return match[1];
  }
  return null;
}

/**
 * Extrahiert Rechnungsdatum (DD.MM.YYYY → YYYY-MM-DD)
 */
function extractRechnungsdatum(lines: string[]): string | null {
  for (const line of lines.slice(0, 30)) {
    const match = line.match(/Rechnungsdatum:\s*(\d{2})\.(\d{2})\.(\d{4})/i);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
  }
  return null;
}

/**
 * Extrahiert die Beraternummer aus dem Rechnungskopf.
 * Format: "Beraternummer: 992453". Die Angabe steht im PDF in derselben Zeile wie
 * der Empfaengername, weshalb hier bewusst nicht auf Zeilenanfang geprueft wird.
 */
function extractBeraternummer(lines: string[]): string | null {
  for (const line of lines.slice(0, 30)) {
    const match = line.match(/Beraternummer:\s*(\d+)/i);
    if (match) return match[1];
  }
  return null;
}

/**
 * Summiert die Nettobeträge aus dem Rechnungsfuß.
 *
 * DATEV weist je USt-Satz eine eigene Nettobetrag-Zeile aus (19 % und 7 %),
 * deshalb wird über alle Treffer summiert. Der Wert dient als Gegenprobe gegen
 * die Summe aller Blockkopfsummen und deckt damit Lücken auf, die innerhalb
 * eines Blocks nicht auffallen, etwa einen gar nicht erkannten Mandantenblock.
 *
 * @returns Summe der Nettobeträge, oder null wenn keine Zeile gefunden wurde
 */
function extractNettoSumme(lines: string[]): number | null {
  let summe = 0;
  let gefunden = false;

  for (const line of lines) {
    const match = line.match(NETTO_TOTAL_PATTERN);
    if (!match) continue;
    const wert = parseGermanNumber(match[1]);
    if (isNaN(wert)) continue;
    summe += wert;
    gefunden = true;
  }

  return gefunden ? round2(summe) : null;
}

// ============================================
// MANDANTEN-BLOCK-ERKENNUNG
// ============================================

/**
 * Prüft, ob eine Zeile eine Kategorie- oder Summenzeile ist.
 * Solche Zeilen sind weder Blockkopf noch Posten und dürfen auch nicht als
 * nicht lesbarer Posten gemeldet werden.
 */
function isCategorySummaryLine(line: string): boolean {
  const lower = line.toLowerCase();
  return CATEGORY_SUMMARY_KEYWORDS.some(keyword => lower.includes(keyword.toLowerCase()));
}

/**
 * Prüft, ob eine Zeile ein Mandantenblock-Header ist.
 * Format: "{4-5 Ziffern} {Name mit Leerzeichen} Summe: {Betrag}"
 *
 * Gegenbeispiele (müssen abgelehnt werden):
 * - "Beraterbezogene Leistungen ... Summe: 216,32"
 * - "Summe Personalwirtschaft -584,70"
 * - "Nettobetrag 5.192,68"
 *
 * @returns Objekt mit datevMandantennummer + Name, oder null
 */
function parseMandantenHeader(line: string): { nr: string; name: string } | null {
  // Filter: Kategorie-Zeilen ausschließen
  if (isCategorySummaryLine(line)) {
    return null;
  }

  // Muster: Am Anfang der Zeile 4-5 Ziffern, gefolgt von Name, endet mit "Summe: Betrag"
  const match = line.match(/^(\d{4,5})\s+(.+?)\s+Summe:\s+[-\d.,]+\s*$/);
  if (!match) return null;

  const nr = match[1];
  const name = match[2].trim();

  // Plausibilitäts-Check: Name darf nicht leer sein
  if (!name) return null;

  return { nr, name };
}

/**
 * Prüft, ob eine Zeile der Header des Blocks "Beraterbezogene Leistungen" ist.
 * Format: "Beraterbezogene Leistungen Summe: 1.107,82"
 *
 * Dieser Block steht im PDF VOR dem ersten Mandantenblock und enthält die
 * Kosten der Kanzlei selbst. Er wird nur für die Auswertung erfasst.
 *
 * @returns die DATEV-Kopfsumme, oder null
 */
function parseBeraterBlockHeader(line: string): { summe: number } | null {
  const match = line.match(/^Beraterbezogene\s+Leistungen\b.*?Summe:\s*(-?[\d.,]+)\s*$/i);
  if (!match) return null;
  const summe = parseGermanNumber(match[1]);
  if (isNaN(summe)) return null;
  return { summe };
}

// ============================================
// POSTEN-PARSING
// ============================================

/**
 * Parst eine einzelne Posten-Zeile.
 *
 * Format:
 *   "{Bezeichnung}   {Artikelnr}[{Fußnote}]   {Menge}   {Leistungszeitpunkt}   {USt}%   {Betrag}"
 *
 * Beispiele:
 *   "DATEV Belege online 95157 1 Aug. 2026 19 % 3,83"
 *   "Abrechnungsgrundpreis 42181 18 09.03.2026 19 % 26,46"
 *   "DATEV Basis-Angebot 50010⁴ 7 Aug. 2026 19 % 641,71"
 *   "DATEV IT-Service- und Sicherheitspauschale 20640¹ 1 Aug. 2026 19 % 2,10"
 *
 * Leistungszeitpunkt siehe TIME_POINT_PATTERN, Fußnoten siehe FOOTNOTE_PATTERN.
 *
 * @returns geparste Position, oder null wenn nicht parsbar
 */
function parsePositionLine(line: string): ParsedInvoicePosition | null {
  const regex = new RegExp(
    `^(.+?)\\s+(\\d{4,5})${FOOTNOTE_PATTERN}\\s+(-?\\d+)\\s+(${TIME_POINT_PATTERN})\\s+(\\d+)\\s*%\\s+(-?[\\d.,]+)$`,
    'i'
  );

  const match = line.match(regex);
  if (!match) return null;

  const bezeichnung = match[1].trim();
  const artikelnr = match[2];
  const menge = parseInt(match[3], 10);
  const leistungszeitpunkt = match[4].trim();
  const ustSatz = parseInt(match[5], 10);
  const gesamtpreis = parseGermanNumber(match[6]);

  if (isNaN(menge) || menge === 0 || isNaN(gesamtpreis) || isNaN(ustSatz)) {
    return null;
  }

  const einzelpreisBerechnet = menge !== 0 ? gesamtpreis / menge : 0;

  return {
    artikelnr,
    bezeichnung,
    menge,
    leistungszeitpunkt,
    einzelpreisBerechnet,
    gesamtpreis,
    ustSatz
  };
}

/**
 * Parst eine Pauschalzeile ohne Artikelnummer und ohne USt-Satz.
 *
 * Format:
 *   "{Bezeichnung} lt. Anlage {Leistungszeitpunkt} {Betrag}"
 *
 * Beispiel:
 *   "Abrechnungsbonus Lohn lt. Anlage Aug. 2026 -989,00"
 *
 * Solche Zeilen haben keine Artikelnummer. Das Feld bleibt deshalb leer, wodurch
 * sie niemals whitelistfähig und niemals weiterberechenbar sind. Menge wird mit 1
 * und USt-Satz mit 0 abgebildet, weil die DATEV-Rechnung hier keine Werte ausweist.
 *
 * @returns geparste Position, oder null wenn nicht parsbar
 */
function parsePauschalLine(line: string): ParsedInvoicePosition | null {
  const regex = new RegExp(
    `^(.+?)\\s+lt\\.\\s*Anlage\\s+(${TIME_POINT_PATTERN})\\s+(-?[\\d.,]+)$`,
    'i'
  );

  const match = line.match(regex);
  if (!match) return null;

  const bezeichnung = match[1].trim();
  const leistungszeitpunkt = match[2].trim();
  const gesamtpreis = parseGermanNumber(match[3]);

  if (!bezeichnung || isNaN(gesamtpreis)) return null;

  return {
    artikelnr: '',
    bezeichnung,
    menge: 1,
    leistungszeitpunkt,
    einzelpreisBerechnet: gesamtpreis,
    gesamtpreis,
    ustSatz: 0
  };
}

/**
 * Parst eine deutsche Zahl ("1.234,56" oder "1234,56" oder "-726,20")
 */
function parseGermanNumber(s: string): number {
  const normalized = s.replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized);
}

/**
 * Rundet auf zwei Dezimalstellen (Schutz vor Gleitkomma-Artefakten beim Addieren)
 */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ============================================
// HAUPT-PARSER
// ============================================

/**
 * Haupt-Einstiegspunkt: Parst eine DATEV-Rechnung (PDF-Datei) und gibt
 * die strukturierten Daten zurück.
 */
export const datevInvoiceParserService = {
  /**
   * Parst eine DATEV-Rechnung PDF-Datei.
   */
  async parseInvoicePdf(file: File): Promise<ParsedDatevInvoice> {
    // PDF laden
    const arrayBuffer = await file.arrayBuffer();
    const pdfData = new Uint8Array(arrayBuffer);
    const loadingTask = pdfjsLib.getDocument({ data: pdfData });
    const pdf = await loadingTask.promise;

    // Alle Seiten Zeile für Zeile extrahieren
    const allLines: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const pageLines = await extractPageLines(pdf, pageNum);
      allLines.push(...pageLines);
    }

    console.log(`[DatevParser] PDF hat ${pdf.numPages} Seite(n), ${allLines.length} Zeilen extrahiert`);

    // Header-Daten extrahieren
    const monthYear = extractMonthYear(allLines);
    if (!monthYear) {
      throw new Error(
        'Abrechnungsmonat konnte nicht aus der PDF extrahiert werden. ' +
        'Erwartetes Format im Header: "<Monatsname> <Jahr>" (z.B. "März 2026").'
      );
    }

    const rechnungsnummer = extractRechnungsnummer(allLines);
    const rechnungsdatum = extractRechnungsdatum(allLines);
    const beraternummer = extractBeraternummer(allLines);
    const nettoSummeLautPdf = extractNettoSumme(allLines);

    console.log(
      `[DatevParser] Header: ${monthYear.month}/${monthYear.year}, ` +
      `Rechnung ${rechnungsnummer || '(unbekannt)'}, Berater ${beraternummer || '(unbekannt)'}, ` +
      `Nettobetrag laut Fuß ${nettoSummeLautPdf !== null ? nettoSummeLautPdf.toFixed(2) : '(nicht gelesen)'}`
    );

    // Mandantenblöcke + Posten parsen
    const parsedBloecke = parseMandantenBlocks(allLines);

    // Eigenen Bestand dem beraterbezogenen Block zuschlagen
    const mandanten = mergeEigeneFirmaInBeraterBlock(parsedBloecke, beraternummer);

    const ungeparstGesamt = mandanten.reduce((sum, m) => sum + m.ungeparsteZeilen.length, 0);
    console.log(`[DatevParser] ${mandanten.length} Block/Blöcke gefunden`);
    if (ungeparstGesamt > 0) {
      console.warn(
        `[DatevParser] ${ungeparstGesamt} Zeile(n) sehen wie ein Posten aus, konnten aber nicht ` +
        'gelesen werden. Die Weiterberechnung wird deshalb blockiert.'
      );
      for (const m of mandanten) {
        for (const zeile of m.ungeparsteZeilen) {
          console.warn(`[DatevParser]   ${m.datevMandantennummer}: ${zeile}`);
        }
      }
    }

    return {
      month: monthYear.month,
      year: monthYear.year,
      rechnungsnummer,
      rechnungsdatum,
      beraternummer,
      mandanten,
      nettoSummeLautPdf
    };
  }
};

/**
 * Geht alle Zeilen durch, erkennt den beraterbezogenen Block sowie
 * Mandantenblock-Header und sammelt die zugehörigen Posten bis zum nächsten
 * Block-Header (oder EOF).
 *
 * Zeilen, die wie ein Posten aussehen, aber auf kein Muster passen, werden im
 * Block als ungeparste Zeile vermerkt statt stillschweigend verworfen.
 */
function parseMandantenBlocks(lines: string[]): ParsedMandant[] {
  const mandanten: ParsedMandant[] = [];
  let current: ParsedMandant | null = null;
  let beraterBlockErfasst = false;

  for (const line of lines) {
    // Versuche Mandantenblock-Header zu matchen
    const header = parseMandantenHeader(line);
    if (header) {
      // Vorherigen Block abschließen
      if (current) {
        mandanten.push(current);
      }
      // Neuen Block starten
      current = {
        datevMandantennummer: header.nr,
        datevName: header.name,
        positionen: [],
        gesamtbetragLautDatev: extractSummeFromHeader(line),
        istBeraterBlock: false,
        zusammensetzung: [],
        ungeparsteZeilen: []
      };
      continue;
    }

    // Beraterbezogener Block (steht vor dem ersten Mandantenblock, nur einmal)
    if (!beraterBlockErfasst) {
      const beraterHeader = parseBeraterBlockHeader(line);
      if (beraterHeader) {
        if (current) {
          mandanten.push(current);
        }
        current = {
          datevMandantennummer: BERATER_BLOCK_KEY,
          datevName: BERATER_BLOCK_LABEL,
          positionen: [],
          gesamtbetragLautDatev: beraterHeader.summe,
          istBeraterBlock: true,
          zusammensetzung: [],
          ungeparsteZeilen: []
        };
        beraterBlockErfasst = true;
        continue;
      }
    }

    // Innerhalb eines Blocks: Posten parsen
    if (current) {
      const position = parsePositionLine(line) || parsePauschalLine(line);
      if (position) {
        current.positionen.push(position);
        continue;
      }

      // Nicht geparst: Sieht die Zeile trotzdem wie ein Posten aus, ist das ein
      // Befund. Kategorie- und Summenzeilen sind hier ausdrücklich ausgenommen.
      if (!isCategorySummaryLine(line) && CANDIDATE_POSITION_PATTERN.test(line)) {
        current.ungeparsteZeilen.push(line);
      }
    }
  }

  // Letzten Block abschließen
  if (current) {
    mandanten.push(current);
  }

  return mandanten;
}

/**
 * Schlägt den eigenen DATEV-Bestand von XTAX dem beraterbezogenen Block zu.
 *
 * Bedingungen (beide müssen erfüllt sein):
 * - Die Beraternummer aus dem Rechnungskopf ist die eigene
 * - Es existiert sowohl ein beraterbezogener Block als auch der eigene Mandantenblock
 *
 * Ist eine Bedingung nicht erfüllt, bleibt alles unverändert. Insbesondere bleibt der
 * eigene Mandantenblock ein normaler Block, wenn die Rechnung keinen beraterbezogenen
 * Block enthält – dann fehlt das Ziel für die Zusammenführung.
 *
 * Die Kopfsummen werden addiert, damit die Kontrolle "gelesene Posten gegen Kopfsumme"
 * weiter aufgeht. Die beiden Teilbeträge bleiben in `zusammensetzung` erhalten, damit
 * der Bezug zur DATEV-Rechnung nachvollziehbar ist.
 */
function mergeEigeneFirmaInBeraterBlock(
  bloecke: ParsedMandant[],
  beraternummer: string | null
): ParsedMandant[] {
  if (beraternummer !== EIGENE_FIRMA_BERATERNUMMER) return bloecke;

  const beraterBlock = bloecke.find(b => b.istBeraterBlock);
  const eigenerBlock = bloecke.find(
    b => !b.istBeraterBlock && b.datevMandantennummer === EIGENE_FIRMA_MANDANTENNUMMER
  );

  if (!beraterBlock || !eigenerBlock) return bloecke;

  beraterBlock.zusammensetzung = [
    {
      bezeichnung: 'Beraterbezogene Leistungen laut DATEV',
      summe: round2(beraterBlock.gesamtbetragLautDatev)
    },
    {
      bezeichnung: `Mandant ${eigenerBlock.datevMandantennummer} ${eigenerBlock.datevName}`,
      summe: round2(eigenerBlock.gesamtbetragLautDatev)
    }
  ];
  beraterBlock.positionen = [...beraterBlock.positionen, ...eigenerBlock.positionen];
  beraterBlock.ungeparsteZeilen = [
    ...beraterBlock.ungeparsteZeilen,
    ...eigenerBlock.ungeparsteZeilen
  ];
  beraterBlock.gesamtbetragLautDatev = round2(
    beraterBlock.gesamtbetragLautDatev + eigenerBlock.gesamtbetragLautDatev
  );

  console.log(
    `[DatevParser] Eigener Bestand ${eigenerBlock.datevMandantennummer} dem beraterbezogenen ` +
    `Block zugeschlagen (neue Kopfsumme ${beraterBlock.gesamtbetragLautDatev.toFixed(2)})`
  );

  return bloecke.filter(b => b !== eigenerBlock);
}

/**
 * Extrahiert den "Summe: XX,XX" Betrag aus einem Mandantenblock-Header
 */
function extractSummeFromHeader(line: string): number {
  const match = line.match(/Summe:\s+(-?[\d.,]+)/);
  if (!match) return 0;
  return parseGermanNumber(match[1]);
}
